import axios, { type InternalAxiosRequestConfig } from 'axios';
import { installHtmlResponseGuard } from '@lumi/app-kit';

// Per-request opt-out for the global notice surface. This app no longer HAS a
// global notice surface of its own (see the response interceptor below), so the
// flag is inert here — it stays because app-kit still reads it on the request
// config type and the packages that have not converted yet still set it (issue
// 195, A15). It goes when the last one flips.
declare module 'axios' {
  export interface AxiosRequestConfig {
    suppressGlobalError?: boolean;
  }
}

// account.{LUMI_DOMAIN} serves this app AND user-api's `/api/user` (D27), so the
// client is same-origin by construction: a relative base URL, no CORS story, no
// build-time API host to get wrong.
export const accountApi = axios.create({
  baseURL: '/api/user',
});

// The account host is a DIFFERENT ORIGIN from every tenant host, so it cannot see
// (and must not assume) a workspace session. Its own key makes that explicit:
// signing in here is not signing in to a workspace, and signing out here does not
// touch one.
export const ACCOUNT_TOKEN_KEY = 'kb_account_token';

// localStorage throws in a locked-down browser (private mode, blocked cookies).
// The account host is the surface someone reaches when everything else already
// went wrong, so a storage failure must degrade to a session that lasts for this
// page, never to a blank screen.
export function readAccountToken(): string {
  try {
    return localStorage.getItem(ACCOUNT_TOKEN_KEY) || '';
  } catch {
    return '';
  }
}

export function storeAccountToken(token: string) {
  try {
    localStorage.setItem(ACCOUNT_TOKEN_KEY, token);
  } catch {
    // storage blocked — the token still lives in the store for this visit
  }
}

export function clearAccountToken() {
  try {
    localStorage.removeItem(ACCOUNT_TOKEN_KEY);
  } catch {
    // storage blocked — nothing was persisted to remove
  }
}

// The auth store registers here so a dead session ends in one place. A callback
// rather than an import keeps the dependency one-way (store → api), which is what
// lets this module be unit-tested without pinia.
let sessionLostHandler: (() => void) | null = null;

export function onAccountSessionLost(handler: () => void) {
  sessionLostHandler = handler;
}

accountApi.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = readAccountToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // No `X-Tenant-Slug`, ever, and no host-derived tenant: authentication here is
  // PERSON-FIRST (D40) and every tenant-specific question is asked per request, by
  // naming the workspace in the path.
  return config;
});

accountApi.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err.response?.status;
    const url = err.config?.url || '';
    if (status === 401 && !url.includes('/auth/login')) {
      // A dead session, not bad credentials. There is nowhere to bounce to — the
      // account host has no workspace to hand the visitor back to — so the token is
      // dropped and the app falls back to its own sign-in card.
      clearAccountToken();
      sessionLostHandler?.();
    }
    // A 403 is deliberately NOT special-cased: on this host it is a real answer
    // about one workspace (not a member, or the workspace mandates SSO), and the
    // caller renders it. Bouncing the session on it would log people out of their
    // own account because of one workspace they may not act on.
    // NOTHING is surfaced from here (issue 195, ADR 0068 rule 8 / A10). Reads report
    // in the surface that was loading, and every write in this app now goes through
    // `useApiMutation`, which owns the failure channel for it. An interceptor that
    // still spoke would say it a second time, next to the message the sign-in card or
    // the erasure dialog already renders.
    return Promise.reject(err);
  },
);

// HTML-instead-of-JSON guard (#317 A73). A misrouted request — a wrong VITE_*
// base URL, a proxy rule that fell through, an ingress serving the SPA shell for
// an /api path — answers 200 with an HTML error page. Without this, axios throws
// mid-parse or (in the copy that once dropped the check) resolves it as a
// SUCCESSFUL response, so a broken deploy looks healthy from the browser. Both
// halves ship together from app-kit, because installing one without the other is
// the failure mode itself.
installHtmlResponseGuard(accountApi);
